/**
 * Game Engine - Core state machine for Simon Revolution
 * 
 * States: IDLE → SHOW → INPUT → VALIDATE → RESULT → TRANSITION → IDLE
 * 
 * Special modes:
 * - PANIC_MODE: activated when response time < 50% of available time
 * - MIRROR_MODE: sequence transformation (reverse/mirror/both)
 * - INVISIBLE_MODE: buttons opacity = 0, audio-guided
 */

export type GameState = 
  | 'idle'
  | 'showSequence'
  | 'countIn'
  | 'waitInput'
  | 'validate'
  | 'result'
  | 'transition'
  | 'gameOver';

export type GameMode = 'classic' | 'chaos' | 'rhythm' | 'puzzle';

export interface IntruderConfig {
  enabled: boolean;
  baseSpawnChance: number; // 0.0 to 1.0, chance per second during input
  maxIntruders: number;
  levelScaling: number; // multiplier per level
  penaltyType: 'end' | 'deduct'; // 'end' = game over, 'deduct' = lose points
  penaltyAmount?: number; // points to deduct if penaltyType is 'deduct'
}

export interface RhythmConfig {
  enabled: boolean;
  toleranceMs: number; // Timing tolerance in milliseconds
  beatIntervalMs: number; // Time between beats (e.g., 600ms = 100 BPM)
}

export interface WearConfig {
  enabled: boolean;
  minUses: number; // Minimum uses before exhaustion
  maxUses: number; // Maximum uses before exhaustion
  visualThreshold: number; // Wear level at which visual degradation starts (0.0-1.0)
}

export interface PanicConfig {
  enabled: boolean;
  triggerThreshold: number; // 0.0-1.0, fraction of available time (e.g., 0.5 = respond within 50% of time)
  speedMultiplier: number; // Speed increase factor when panic active (e.g., 1.5 = 50% faster)
  scoreMultiplier: number; // Score bonus multiplier (e.g., 2.0 = double points)
  minStreak: number; // Minimum consecutive fast responses to activate panic
}

export interface GameConfig {
  mode: GameMode;
  speed: 'slow' | 'normal' | 'fast';
  maxButtons: number;
  enablePhysics: boolean;
  enableIntruders: boolean;
  enableRhythm: boolean;
  enableInvisible: boolean;
  enableWear: boolean;
  enablePanic: boolean;
  intruderConfig?: IntruderConfig;
  rhythmConfig?: RhythmConfig;
  wearConfig?: WearConfig;
  panicConfig?: PanicConfig;
}

export class GameEngine {
  private state: GameState = 'idle';
  private config: GameConfig;
  
  constructor(config: Partial<GameConfig> = {}) {
    this.config = {
      mode: 'classic',
      speed: 'normal',
      maxButtons: 4,
      enablePhysics: false,
      enableIntruders: false,
      enableRhythm: false,
      enableInvisible: false,
      enableWear: false,
      enablePanic: false,
      intruderConfig: {
        enabled: false,
        baseSpawnChance: 0.15, // 15% chance per second
        maxIntruders: 2,
        levelScaling: 0.05, // +5% per level
        penaltyType: 'end',
        penaltyAmount: 50
      },
      rhythmConfig: {
        enabled: false,
        toleranceMs: 150, // Default tolerance: 150ms
        beatIntervalMs: 600 // Default: 100 BPM
      },
      wearConfig: {
        enabled: false,
        minUses: 15,
        maxUses: 30,
        visualThreshold: 0.4
      },
      panicConfig: {
        enabled: false,
        triggerThreshold: 0.5, // Respond within 50% of available time
        speedMultiplier: 1.3, // 30% faster
        scoreMultiplier: 1.5, // 50% bonus points
        minStreak: 3 // Need 3 fast responses to activate
      },
      ...config
    };
  }

  getState(): GameState {
    return this.state;
  }

  setState(newState: GameState): void {
    this.state = newState;
  }

  getConfig(): GameConfig {
    return this.config;
  }

  updateConfig(updates: Partial<GameConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  getIntruderSpawnChance(level: number): number {
    if (!this.config.intruderConfig?.enabled) return 0;
    const base = this.config.intruderConfig.baseSpawnChance;
    const scaling = this.config.intruderConfig.levelScaling;
    return Math.min(0.8, base + (level - 1) * scaling); // cap at 80%
  }
}
